import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { Button, Card, CardBody, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import ActiveHelper from '../../../components/common/activeHelper';
import { Colxx } from '../../../components/common/CustomBootstrap';
import { ORDER_COLLECTION } from '../../../config/env';
import { firestore } from '../../../helpers/Firebase';
import { accessTypes, accessTypesObject, deviceWidth } from '../../../helpers/Utils';
import AddNewAppointment from './components/AddNewAppointment';
import Order from './components/order';

const Appointments = () => {
  // const { bookings, washers, provider, activeServices } = useSelector(state => state.providers);
  const { provider, washers, bookings, services, advServices, } = useSelector(state => state.providers)

  const { user } = useSelector(state => state.authUser);
  const search = useSelector(state => state.settings.search) || '';
  const [selectedBooking, setSelectedBooking] = useState(false);
  const [activeServices, setActiveServices] = useState({});
  const [filters, setFilters] = useState({
    status: ['accepted', 'active', 'completed', 'notRated'],
    statusKey: 'appointments'
  });
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [createNewOrder, setCreateNewOrder] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment().toDate());
  const [delayedAction, setDelayedAction] = useState(false);

  // Comprehensive filtering logic
  useEffect(() => {
    // Separate delayed and non-delayed bookings
    const allBookings = Object.values(bookings || {})
      ?.filter(rec => !['new', 'pending', 'cancelled', 'rejected','canceled'].includes(rec.status));

    const delayedBookings = allBookings.filter(rec => rec.delayed);
    let nonDelayedBookings = allBookings.filter(rec => !rec.delayed);

    // Apply date filter to non-delayed bookings only
    if (selectedDate) {
      nonDelayedBookings = nonDelayedBookings.filter(rec =>
        moment(rec.bookingDate).format('DD/MM/YYYY') === moment(selectedDate).format('DD/MM/YYYY')
      );
    }

    // Filter by providers - only apply if providers filter has items
    if (filters?.providers && filters.providers.length > 0) {
      nonDelayedBookings = nonDelayedBookings.filter(rec =>
        filters.providers.includes(rec.sp_id)
      );
    }

    // Filter by services - only apply if services filter has items
    if (filters?.services && filters.services.length > 0) {
      nonDelayedBookings = nonDelayedBookings.filter(rec =>
        filters.services.includes(rec.service_id)
      );
    }

    // Filter by washers - only apply if washers filter has items
    if (filters?.washers && filters.washers.length > 0) {
      nonDelayedBookings = nonDelayedBookings.filter(rec =>
        filters.washers.includes(rec.washer_id)
      );
    }

    // Filter by status (additional status filtering if needed)
    if (filters?.status && filters.status.length > 0) {
      nonDelayedBookings = nonDelayedBookings.filter(rec =>
        filters.status.includes(rec.status)
      );
    }

    // Search filter - search across multiple fields (only for non-delayed)
    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      nonDelayedBookings = nonDelayedBookings.filter(rec => {
        return (
          (rec?.customer_name?.toLowerCase().includes(searchLower)) ||
          (rec?.customer_number?.toLowerCase().includes(searchLower)) ||
          (rec?.service_name?.toLowerCase().includes(searchLower)) ||
          (rec?.washer_name?.toLowerCase().includes(searchLower)) ||
          (rec?.sp_name?.toLowerCase().includes(searchLower)) ||
          (rec?.status?.toLowerCase().includes(searchLower))
        );
      });
    }

    // Sort: delayed first (by bookingDate), then non-delayed (by bookingDate)
    delayedBookings.sort((a, b) => a.bookingDate - b.bookingDate);
    nonDelayedBookings.sort((a, b) => a.bookingDate - b.bookingDate);

    // Add delay text and sortPriority to delayed bookings for display
    const delayedWithText = delayedBookings.map(booking => ({
      ...booking,
      delayText: moment().to(moment(booking.bookingDate)).replace(' ago', ' Delay'),
      sortPriority: 0 // Delayed orders get priority 0 (sort first)
    }));

    // Add sortPriority to non-delayed bookings
    const nonDelayedWithKey = nonDelayedBookings.map(booking => ({
      ...booking,
      sortPriority: 1 // Non-delayed orders get priority 1 (sort after)
    }));

    // Combine: delayed orders always appear first
    const filteredBookings = [...delayedWithText, ...nonDelayedWithKey];

    setFilteredBookings(filteredBookings);
  }, [filters, bookings, search, selectedDate]);
  useEffect(() => {
    let allServices = Object.values(services || {})?.concat(Object.values(advServices || {}))
    const updatedActiveServices = {};
    Object.values(washers || {}).forEach(washer => {
      if (washer.activeServices) {
        const activeServiceKeys = Object.keys(washer.activeServices);
        const filteredServices = allServices.filter(service => activeServiceKeys.includes(service.key));
        filteredServices.forEach(service => {
          updatedActiveServices[service.key] = {
            ...service,
            modifier: washer.activeServices[service.key],
            label: service.name,
            value: service.name
          };
        });
      }
    });
    setActiveServices((prevActiveServices) => {
      return { ...prevActiveServices, ...updatedActiveServices };
    });
  }, [services, advServices, washers]);

  const tableColumns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "sortPriority",
        show: false,
        sortMethod: (a, b) => {
          // Delayed orders (priority 0) always come before non-delayed (priority 1)
          // If same priority, maintain order
          return a - b;
        }
      },
      {
        Header: "Booking Date",
        accessor: "bookingDate",
        // width: deviceWidth() === 'desktop' ? 120 : 80, 
        type: 'appointmentDate'
      },
      { Header: "Service Provider", accessor: "sp_name", width: 150, type: 'label', show: accessTypes.includes(user?.accessType) },
      {
        Header: "Customer",
        accessor: "customer_name",
        // width: 150, 
        type: 'customer',
        showCustomerNumber: accessTypes.includes(user?.accessType)
      },
      {
        Header: "Service Name",
        accessor: "service_name",
        // width: 150, 
        type: 'label',
        show: deviceWidth() === 'desktop'
      },
      {
        Header: "Worker Name",
        accessor: "washer_name",
        width: 150,
        type: 'label',
        show: deviceWidth() === 'desktop'
      },
      {
        Header: "Status", accessor: "status", type: 'orderStatus',
        // width: 150
      },
      {
        Header: "Type",
        accessor: "type",
        type: 'orderType',
        // width: 150,
        show: deviceWidth() === 'desktop'
      },
      // { 
      //   Header: "Delay", 
      //   accessor: "delayText", 
      //   // width: 120, 
      //   type: 'badge',
      //   color: 'danger',
      //   show: deviceWidth() === 'desktop'
      // },


    ];
  }, [user?.accessType]);

  const toggleModal = () => {
    setSelectedBooking(false)
  };
  const dismiss = () => {
    setSelectedBooking(false)
  };

  // Create filter components for filtering
  const filterComponent = useMemo(() => {
    const filterComponents = [];

    // Date filter with navigation arrows
    filterComponents.push(
      <Colxx xxs="12" md="3" key="date" className="d-flex align-items-center mb-3" >
        <div className="d-flex align-items-center" style={{ gap: '8px', width: '100%' }}>
          <i
            className="simple-icon-arrow-left"
            style={{ cursor: 'pointer', fontSize: '16px' }}
            onClick={() => setSelectedDate(moment(selectedDate).subtract(1, "days").toDate())}
          />
          <DatePicker
            selected={selectedDate}
            // className="form-control"
            onChange={date => setSelectedDate(date)}
            dateFormat="dd/MM/yyyy"
            style={{ flex: 1 }}
          />
          <i
            className="simple-icon-arrow-right"
            style={{ cursor: 'pointer', fontSize: '16px' }}
            onClick={() => setSelectedDate(moment(selectedDate).add(1, "days").toDate())}
          />
        </div>
      </Colxx>
    );

    // Service Provider filter (only for admin users)
    if (accessTypes.includes(user?.accessType)) {
      const providerOptions = Object.values(provider || {}).map(prov => ({
        label: prov.name || prov.label,
        value: prov.key,
        key: prov.key
      }));

      if (providerOptions.length > 0) {
        filterComponents.push(
          <Colxx xxs="12" md="3" key="providers" className='mb-3' >
            <Select
              isClearable={true}
              isMulti={true}
              placeholder="Filter by Service Providers"
              name="providers"
              options={providerOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              value={filters?.providers && filters.providers.length > 0
                ? providerOptions.filter(p => filters.providers.includes(p.key))
                : []
              }
              onChange={val => {
                setFilters(prev => ({
                  ...prev,
                  providers: (val && val.length > 0) ? val.map(v => v.key) : []
                }));
              }}
            />
          </Colxx>
        );
      }
    }

    // Services filter
    const serviceOptions = Object.values(activeServices || {}).map(service => ({
      label: service.name || service.label,
      value: service.key,
      key: service.key
    }));

    if (serviceOptions.length > 0) {
      filterComponents.push(
        <Colxx xxs="12" md="3" key="services" className='mb-3' >
          <Select
            isClearable={true}
            isMulti={true}
            placeholder="Filter by Services"
            name="services"
            options={serviceOptions}
            className="basic-multi-select"
            classNamePrefix="select"
            value={filters?.services && filters.services.length > 0
              ? serviceOptions.filter(s => filters.services.includes(s.key))
              : []
            }
            onChange={val => {
              setFilters(prev => ({
                ...prev,
                services: (val && val.length > 0) ? val.map(v => v.key) : []
              }));
            }}
          />
        </Colxx>
      );
    }

    // Washers filter
    const washerOptions = Object.values(washers || {}).map(washer => ({
      label: washer.name || washer.label,
      value: washer.key,
      key: washer.key
    }));

    if (washerOptions.length > 0) {
      filterComponents.push(
        <Colxx xxs="12" md="3" key="washers" className='mb-3' >
          <Select
            isClearable={true}
            isMulti={true}
            placeholder="Filter by Workers"
            name="washers"
            options={washerOptions}
            className="basic-multi-select"
            classNamePrefix="select"
            value={filters?.washers && filters.washers.length > 0
              ? washerOptions.filter(w => filters.washers.includes(w.key))
              : []
            }
            onChange={val => {
              setFilters(prev => ({
                ...prev,
                washers: (val && val.length > 0) ? val.map(v => v.key) : []
              }));
            }}
          />
        </Colxx>
      );
    }

    // Status filter - Single select with grouped statuses
    const statusOptions = [
      { label: 'All Appointments', value: ['accepted', 'active', 'completed', 'notRated'], key: 'appointments' },
      { label: 'Accepted', value: ['accepted'], key: 'accepted' },
      { label: 'Active', value: ['active'], key: 'active' },
      { label: 'Completed', value: ['completed', 'notRated'], key: 'completed' },
    ];

    filterComponents.push(
      <Colxx xxs="12" md="3" key="status" className='mb-3' >
        <Select
          isClearable={true}
          placeholder="Filter by Status"
          name="status"
          options={statusOptions}
          className="basic-multi-select"
          classNamePrefix="select"
          value={filters?.statusKey
            ? statusOptions.find(s => s.key === filters.statusKey)
            : null
          }
          onChange={val => {
            setFilters(prev => ({
              ...prev,
              status: val ? val.value : [],
              statusKey: val ? val.key : null
            }));
          }}
        />
      </Colxx>
    );

    return filterComponents.length > 0 ? <>{filterComponents}</> : null;
  }, [provider, washers, activeServices, user?.accessType, filters, selectedDate]);

  return (
    <div>
      {createNewOrder ?

        <Card className="rounded h-100">
          <CardBody>
            <AddNewAppointment
              bookings={bookings}
              activeServices={activeServices}
              washers={washers}
              toggleModal={() => setCreateNewOrder(false)}
              dismiss={() => setCreateNewOrder(false)}
            />
          </CardBody>
        </Card>

        :
        <>
          {selectedBooking && <Order
            bookings={bookings}
            washers={washers}
            providers={provider}
            activeServices={activeServices}
            toggleModal={toggleModal}
            dismiss={dismiss}
            selectedBooking={JSON.parse(JSON.stringify(selectedBooking))}
          />}
          <Modal
            isOpen={!!delayedAction}
            backdrop="static"
          >
            <ModalHeader>Update Appointment</ModalHeader>
            <ModalBody>
              <div>Are you sure you want to update the status for this appointment?</div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="danger"
                outline
                onClick={() => {
                  setDelayedAction(false);
                }}
              >
                Dismiss
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  if (!delayedAction) return;

                  let newStatus = (
                    delayedAction.status === 'pending' || delayedAction.status === 'new'
                      ? 'accepted'
                      : delayedAction.status === 'accepted'
                        ? 'active'
                        : delayedAction.status === 'active'
                          ? 'completed'
                          : ''
                  );

                  firestore().collection(ORDER_COLLECTION).doc(delayedAction.key).update({
                    status: newStatus,
                    updated: Date.now()
                  }).then(function () {
                    firestore().collection('logs').doc(delayedAction.key).set({
                      data: firestore.FieldValue.arrayUnion({
                        title: 'statusChanged',
                        created: Date.now(),
                        oldsStatus: delayedAction.status,
                        status: newStatus,
                        createdBy: accessTypesObject?.[user?.accessType]?.name,
                        createdByName: user?.displayName
                      }),
                      updated: Date.now()
                    }, { merge: true }).then(function () {
                      setDelayedAction(false);
                    }).catch(error => {
                      console.error('Error updating logs:', error);
                      setDelayedAction(false);
                    });
                  }).catch(error => {
                    console.error('Error updating appointment:', error);
                    setDelayedAction(false);
                  });
                }}
              >
                {delayedAction?.status === 'pending' || delayedAction?.status === 'new'
                  ? "Accept"
                  : delayedAction?.status === 'accepted'
                    ? "Start Service"
                    : delayedAction?.status === 'active'
                      ? "Complete"
                      : ''}
              </Button>
            </ModalFooter>
          </Modal>
          <ActiveHelper
            data={filteredBookings || []}
            title="Appointments"
            selected='bookings'
            tableColumns={tableColumns}
            defaultSorting="sortPriority"
            filters={filters}
            filterComponent={filterComponent}
            sortDirection={'asc'}
            renderAsModal={true}
            hideDefaultDeleteButton={true}
            modalSize='lg'
            modalBackdrop='static'
            customEditTitle={(e) => {
              // Return button title based on delayed status
              if (e?.delayed === "delayedActive") {
                return 'Start Service';
              } else if (e?.delayed === "delayedCompleted" && !e?.recoveryInfo) {
                return 'Complete';
              }
              return 'View';
            }}
            customEditFunction={(e) => {
              if (!e) return;

              // Handle delayed orders with quick actions (button click)
              if (e.delayed) {
                switch (e.delayed) {
                  case "delayedActive":
                    return setDelayedAction(e);
                  case "delayedCompleted":
                    if (e.recoveryInfo) {
                      return setSelectedBooking(e);
                    } else {
                      return setDelayedAction(e);
                    }
                  default:
                    setSelectedBooking(e);
                }
              } else {
                setSelectedBooking(e);
              }
            }}
            customRowClickFunction={(e) => {
              // Row click always opens order details, even for delayed orders
              if (e) {
                setSelectedBooking(e);
              }
            }}
            showCreateButton={!accessTypes.includes(user?.accessType)}
            customCreateFunction={(e) => {
              console.log(e)
              setCreateNewOrder(true)
            }}
          />
        </>
      }
    </div>
  );
};

export default Appointments;
